import { useEffect } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { formatDate, organizationLegalTextTypes } from '@brimo/shared';
import { Skeleton } from '../../ds/components';
import { ApiError, api } from '../../lib/api';
import { LoadError } from '../onboarding/admin/LoadError';
import type { PublicLegalText } from '../onboarding/admin/types';
import styles from './LegalPage.module.css';

/** Keeps the page out of search engines (`noindex`) while mounted. */
function useNoIndex(): void {
  useEffect(() => {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const created = meta === null;
    if (meta === null) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      document.head.appendChild(meta);
    }
    const previous = meta.content;
    meta.content = 'noindex,nofollow';
    return () => {
      if (created) meta.remove();
      else meta.content = previous;
    };
  }, []);
}

/**
 * The public legal text of an organization (`/legal/<slug>/<type>`, linked from the portal, booking pages and mails):
 * the pasted text as paragraphs or the PDF inline with a download link; unpublished texts answer the 404 page.
 */
export function LegalPage() {
  useNoIndex();
  const { orgSlug = '', type = '' } = useParams();
  const known = (organizationLegalTextTypes as readonly string[]).includes(type);
  const text = useQuery({
    queryKey: ['public-legal', orgSlug, type],
    queryFn: () =>
      api<PublicLegalText>(`/public/legal/${encodeURIComponent(orgSlug)}/${encodeURIComponent(type)}`),
    enabled: known,
    retry: false,
  });

  useEffect(() => {
    if (text.data) document.title = `${text.data.title} · ${text.data.organizationName}`;
  }, [text.data]);

  const notFound = !known || (text.error instanceof ApiError && text.error.status === 404);
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.organization}>{text.data?.organizationName ?? 'DAMOKLES'}</span>
      </header>
      <main className={styles.main}>
        <article className={styles.article}>
          {notFound ? (
            <div className={styles.missing} role="alert">
              <h1 className={styles.title}>Dieser Rechtstext ist nicht veröffentlicht.</h1>
              <p className={styles.missingText}>
                Die Adresse ist falsch oder die Organisation hat den Text noch nicht hinterlegt.
              </p>
            </div>
          ) : text.isPending ? (
            <>
              <Skeleton width={260} height={34} />
              <Skeleton variant="block" height={420} />
            </>
          ) : text.isError ? (
            <LoadError
              what="Der Rechtstext konnte nicht geladen werden"
              error={text.error}
              retrying={text.isFetching}
              onRetry={() => void text.refetch()}
            />
          ) : (
            <>
              <span className={styles.eyebrow}>{text.data.organizationName}</span>
              <h1 className={styles.title}>{text.data.title}</h1>
              <span className={styles.meta}>Stand {formatDate(text.data.updatedAt)}</span>
              {text.data.source === 'pdf' && text.data.file ? (
                <>
                  <object
                    className={styles.pdf}
                    data={text.data.file.url}
                    type="application/pdf"
                    aria-label={text.data.title}
                  >
                    <a href={text.data.file.url} download={text.data.file.name}>
                      {text.data.file.name} herunterladen
                    </a>
                  </object>
                  <a className={styles.download} href={text.data.file.url} download={text.data.file.name}>
                    {text.data.file.name} herunterladen
                  </a>
                </>
              ) : (
                <div className={styles.body}>
                  {(text.data.content ?? '')
                    .split(/\n\s*\n/)
                    .filter((part) => part.trim() !== '')
                    .map((part, index) => (
                      <p key={index} className={styles.paragraph}>
                        {part.trim()}
                      </p>
                    ))}
                </div>
              )}
            </>
          )}
        </article>
      </main>
    </div>
  );
}
